import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { fetchMilharLedger, fetchMilharStatus } from '../../features/milhar/api';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { localDayShort, shortDateTime } from '../../shared/lib/datetime';
import { identityHolds, ledgerDelta, ledgerRowTone, shortHash } from '../../shared/milhar/ledgerView';
import { poolComposition } from '../../shared/milhar/poolCompositionView';
import { formatFlowPct, stakeFlow, winSplit, type FlowSegment } from '../../shared/milhar/poolFlowView';
import { RoundDetailSheet } from '../../features/milhar/RoundDetailSheet';
import { fmtMoney } from '../../shared/lib/format';
import type { MilharTierId } from '../../shared/milhar/tiers';
import type { MessageKey } from '../../shared/i18n/messages';

/**
 * 奖池透明页。视觉规格 §12B，产品口径 master §4.4。
 *
 * <p>开奖在链上可验，但奖池在我们的数据库里 —— 那是整条信任链唯一的薄弱环节。
 * 这一页把它补上：恒等式让「平台一分钱没从奖池里拿」从声明变成任何人可以
 * 自己核对的事实。
 *
 * <p>**恒等式行是全页视觉重心**，不能被流水列表压过去。哈希链那一层是给
 * 会验算的人的，不向普通用户解释。
 */
export function MilharLedgerPage({ tier = '4' }: { tier?: MilharTierId }) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const setRoute = useAppStore((state) => state.setRoute);
  const token = useAuthStore((state) => state.token);
  const countryCode = useCountryStore((state) => state.country.code);

  // 按期分页（每页 50 期，一期可多行）。「每一轮」现在是字面意思：没人投注的
  // 期也是一行，标 0 —— 所以列表长了，要能往前翻。totals 只取首页的。
  const query = useInfiniteQuery({
    queryKey: ['milhar-ledger', tier, token || 'guest', countryCode],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchMilharLedger(token, tier, pageParam),
    getNextPageParam: (last) => last.nextCursor || undefined,
    staleTime: 30_000,
  });
  const firstPage = query.data?.pages[0];
  const entries = query.data?.pages.flatMap((page) => page.entries || []) || [];
  // 也拉一次 status：透明页必须显示玩家在大厅看到的那个数字，否则两页
  // 对不上 —— 而这一页存在的唯一理由就是让数字对得上。
  const statusQuery = useQuery({
    queryKey: ['milhar-status', tier, token || 'guest', countryCode],
    queryFn: () => fetchMilharStatus(token, tier),
    staleTime: 15_000,
  });

  const totals = firstPage?.totals;
  const balanced = totals ? identityHolds(totals) : true;
  const composition = poolComposition(totals);
  // 三个比例全部来自接口 —— 它们是 milhar_economy_config 里按期生效的可调项，
  // 上一期 25%、下一期可能就是 30%。任何一处写死，调参那天页面会继续显示旧比例，
  // 而且没有任何地方会报错。
  const milharConfig = statusQuery.data?.config;
  const stake = stakeFlow(milharConfig?.rtpPct, milharConfig?.jackpotContribPct);
  const win = winSplit(milharConfig?.seedRetainPct);
  // 点开的那一期。§12B.1 要求账本每一行可点进开奖详情与验算。
  const [detailRound, setDetailRound] = useState<number | null>(null);

  // 玩法未启用或接口挂了：这一页的全部意义是「证明钱没被动过」，
  // 拿不到数据时必须明说，不能留一个空壳让人以为看完了。
  const unreachable = !query.isPending && !totals;

  return (
    <section className="ds-screen milhar-ledger-screen">
      <header className="ds-header shrink-0">
        <button
          className="icon-button tap"
          type="button"
          onClick={() => setRoute(tier === '5' ? 'milhar.hora' : 'milhar')}
          aria-label={t('back')}
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="text-[20px] leading-[26px] font-extrabold">{t('milharLedgerTitle')}</h1>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {unreachable ? (
          <p className="milhar-ledger-unreachable">{t('milharLedgerUnavailable')}</p>
        ) : null}

        {/* 构成放在最上面：所有人先看懂这笔钱由什么组成，较真的人再往下看
            恒等式和逐期流水。in / out / adjust 是**对账**的四则运算，不是构成——
            把它们摆在前面，玩家要先做一遍减法才知道池子里有什么。 */}
        {composition ? (
          <section className="milhar-made-of">
            <h2 className="milhar-made-of-title">{t('milharPoolMadeOf')}</h2>
            <div className="milhar-made-of-bar" aria-hidden="true">
              {composition.parts.map((part) => (
                <span key={part.key} className={`is-${part.key}`}
                  style={{ flexGrow: Math.max(part.share, 0.02) }} />
              ))}
            </div>
            <dl className="milhar-made-of-rows">
              <div>
                <dt><i className="is-seed" aria-hidden="true" />
                  {composition.seedOrigin === 'carried'
                    ? t('milharPoolSeedCarried')
                        .replace('%r%', String(composition.seedFromRound))
                    : t('milharPoolSeedInitial')}
                </dt>
                <dd className="mono">
                  {fmtMoney(composition.parts[0].cents / 100)}
                </dd>
              </div>
              <div>
                <dt><i className="is-pot" aria-hidden="true" />{t('milharPoolAccrued')}</dt>
                <dd className="mono">{fmtMoney(composition.parts[1].cents / 100)}</dd>
              </div>
            </dl>
          </section>
        ) : null}

        {/* 每一期，投注怎么分成三份。
            rtpPct 缺席（老后端，或玩法未启用时后端刻意不发）时**整段不渲染** ——
            不能退回「25% / 75%」两分图：那正是这次要消灭的「平台拿走 75%」误读。 */}
        {stake ? (
          <section className="milhar-flow">
            <h2 className="milhar-flow-title">{t('milharFlowStakeTitle')}</h2>
            {/* 这条不是装饰——它是页面上百分比唯一画出来的地方。之前 aria-hidden
                跟旁边真正装饰性的构成条抄错了，把这三个数字对屏幕阅读器彻底藏起来
                （评审 2026-09-11 finding 2）。整条给一个 role="img" + aria-label，
                把「标签 数值」逐段念出来，数值仍然全部来自接口，不写死。 */}
            <div
              className="milhar-flow-bar"
              role="img"
              aria-label={stake.map((seg) => `${t(flowLabelKey(seg))} ${formatFlowPct(seg.pct)}`).join(', ')}
            >
              {stake.map((seg) => (
                <span key={seg.key} className={`is-${seg.key}`} style={{ flexGrow: seg.pct }}>
                  {onBarLabel(seg.pct)}
                </span>
              ))}
            </div>
            {/* 标签贴在自己那一段正下方。flexGrow 与上面那条一一对应，比例一变
                两者一起变 —— 这也是「为什么才 25%」的答案所在：它就写在中间那段
                下面，而那一段比进池的还宽。 */}
            <div className="milhar-flow-legend">
              {stake.map((seg) => (
                <span key={seg.key} style={{ flexGrow: seg.pct }}>{t(flowLabelKey(seg))}</span>
              ))}
            </div>
            <p className="milhar-flow-rtp">
              {t('milharFlowRtp')
                .replace('%base%', fmtMoney(100))
                .replace('%rtp%', fmtMoney(100 - stake[2].pct))}
            </p>
            <p className="milhar-flow-detail">
              {t('milharFlowRtpDetail')
                .replace('%pool%', fmtMoney(stake[0].pct))
                .replace('%fixed%', fmtMoney(stake[1].pct))
                .replace('%house%', fmtMoney(stake[2].pct))}
            </p>
          </section>
        ) : null}

        {win ? (
          <>
            {/* 这句桥接句指的是「上面那条分流图里进池的那份」——只有 stake 那段
                真的画出来了才有先行词。rtpPct 缺席（当前生产环境，后端还没发这个
                字段）或玩法关闭时 stake 是 null，此时不能显示这句话，它会变成
                一句指向空气的孤句（评审 2026-09-11 finding 4）。 */}
            {stake ? <p className="milhar-flow-bridge">{t('milharFlowBridge')}</p> : null}
            <section className="milhar-flow">
              {/* **标题必须带「有人中头奖时」这个限定。** 没人中头奖时整个池子
                  100% 滚到下一期，这个分法根本不发生 —— 把它写成「每一期」正是
                  这次修掉的那个错，不能在新图上再犯一遍。 */}
              <h2 className="milhar-flow-title">{t('milharFlowWinTitle')}</h2>
              {/* 同上：这条也不是装饰，是 80/20 唯一画出来的地方（finding 2）。 */}
              <div
                className="milhar-flow-bar"
                role="img"
                aria-label={win.map((seg) => `${t(flowLabelKey(seg))} ${formatFlowPct(seg.pct)}`).join(', ')}
              >
                {win.map((seg) => (
                  <span key={seg.key} className={`is-${seg.key}`} style={{ flexGrow: seg.pct }}>
                    {onBarLabel(seg.pct)}
                  </span>
                ))}
              </div>
              <div className="milhar-flow-legend">
                {win.map((seg) => (
                  <span key={seg.key} style={{ flexGrow: seg.pct }}>{t(flowLabelKey(seg))}</span>
                ))}
              </div>
              {/* 「图表在顶部」指的是最上面的构成条（.milhar-made-of-bar），不是
                  本页新加的这两条分流图。构成条只在 composition 非空时画出来，
                  而 composition 在 totals 缺席时是 null（游戏关闭/接口挂了）——
                  这时还提「顶部那张图」就是在指一个屏幕上不存在的东西，跟
                  finding 4 里 bridge 句同一种毛病，换成不带图表引用的版本。 */}
              <p className="milhar-flow-detail">
                {t(composition ? 'milharFlowWinHint' : 'milharFlowWinHintNoChart')
                  .replace('%seed%', formatFlowPct(win[1].pct))}
              </p>
            </section>
          </>
        ) : null}

        {totals ? (
          <section className="milhar-ledger-totals">
            <Row label={t('milharLedgerIn')} value={totals.in} />
            <Row label={t('milharLedgerOut')} value={totals.out} />
            <Row label={t('milharLedgerSeed')} value={totals.seed} />
            <Row label={t('milharLedgerPot')} value={totals.pot} />
            {statusQuery.data?.pool ? (
              <>
                <Row label={t('milharLedgerPrize')}
                  value={statusQuery.data.pool.takeHome} strong />
                {/* Without this line the page shows two different "current
                    prize" numbers — the pot (seed + accrued) and the payable
                    share — with nothing connecting them, on the one screen
                    whose whole job is to prove the pool is honest. */}
                <p className="milhar-ledger-prize-hint">{t('milharLedgerPrizeHint')}</p>
              </>
            ) : null}
            {/* 这一行是本页存在的理由 —— 排版上必须是最重的 */}
            <p className={`milhar-ledger-identity${balanced ? '' : ' is-broken'}`}>
              {t('milharLedgerBalanced')} {balanced ? '✓' : '✕'}
            </p>
          </section>
        ) : null}

        <section className="milhar-ledger-entries">
          <h2 className="milhar-ledger-subtitle">{t('milharLedgerEveryRound')}</h2>
          {/* 表头。原来这张表直接甩四列数字，没有一个字说明谁是谁——
              而中间两列语义完全不同：一个是**这一期的进出**，一个是**之后的池子余额**。
              长得一样、挨着放，读者只能靠猜。 */}
          <div className="milhar-ledger-head" aria-hidden="true">
            <span>{t('milharLedgerColRound')}</span>
            <span className="is-num">{t('milharLedgerColChange')}</span>
            <span className="is-num">{t('milharLedgerColBalance')}</span>
            <span />
          </div>
          {entries.map((entry) => (
            <button
              key={`${entry.round}-${entry.kind}`}
              type="button"
              className={`milhar-ledger-row tap tone-${ledgerRowTone(entry.kind)}`}
              onClick={() => setDetailRound(entry.round)}
            >
              {/* 主号用日内序号，与游戏页、注单历史、开奖历史同一套身份
                  （doc/TIME_DISPLAY_CONTRACT.md 规则 1）。账本此前是全站唯一
                  显示全局号的地方，理由是「对账要一个跨天跨时区精确的 ID」——
                  那个需求仍然成立，但它的正确归宿是点开后的详情（抬头 + 可复制
                  芯片，RoundDetailSheet），不是让所有人在列表里读一串五位数。

                  roundLabel **自带 `#`**（MilharTier.roundLabel 返回 "#093"），
                  所以这里不再补 `#`；退回全局号时才自己加。

                  副行从「只有日期」补到分钟，而且时间源变了：以前是 createdAt
                  （结算写账的时刻），现在是 cutoffTs（该期开奖的时刻）——后者才是
                  玩家问的「这一期什么时候开的」。
                  用 shortDateTime 不用 localDateTime：后者写死 en-GB 且带秒。 */}
              <span className="mono milhar-ledger-round">
                {entry.roundLabel || `#${entry.round}`}
                {entry.cutoffTs ? (
                  <em className="milhar-ledger-day">
                    {shortDateTime(entry.cutoffTs, language)}
                  </em>
                ) : entry.createdAt ? (
                  <em className="milhar-ledger-day">{localDayShort(entry.createdAt)}</em>
                ) : null}
              </span>
              {/* 正负号代替原来的「In / Paid」那一列：符号本身就说清了方向，
                  省下一列宽度给两个金额拉开距离。空期（kind NONE）是 0，不带符号。 */}
              <span className={`mono milhar-ledger-amount tone-${ledgerRowTone(entry.kind)}`}>
                {ledgerDelta(entry.amount).sign}{fmtMoney(ledgerDelta(entry.amount).abs)}
              </span>
              <span className="mono milhar-ledger-balance">
                {fmtMoney(Number(entry.balanceAfter))}
              </span>
              <span className="milhar-ledger-chev" aria-hidden="true">
                <ChevronRight size={14} />
              </span>
            </button>
          ))}
          {query.hasNextPage ? (
            <button
              type="button"
              className="milhar-ledger-more tap"
              disabled={query.isFetchingNextPage}
              onClick={() => { void query.fetchNextPage(); }}
            >
              <ChevronDown size={15} aria-hidden="true" />
              {query.isFetchingNextPage ? t('loading') : t('milharLedgerLoadMore')}
            </button>
          ) : null}
        </section>
      </div>

      {detailRound != null ? (
        <RoundDetailSheet
          round={detailRound}
          currency={statusQuery.data?.pool.currency || 'BRL'}
          token={token}
          tier={tier}
          onClose={() => setDetailRound(null)}
        />
      ) : null}
    </section>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`milhar-ledger-total-row${strong ? ' is-strong' : ''}`}>
      <span>{label}</span>
      <b className="mono">{fmtMoney(Number(value))}</b>
    </div>
  );
}

/** 条上文字自己的框太窄时，overflow:hidden 只会裁出半个字符，比不画还难看
 *  （评审 2026-09-11 finding 1 的"验证后续"）——真正裁切的证据：375px 宽、
 *  contribPct=2 时量出该段只有 6px，而 "2%" 需要 18px，画出来是一小截 "%"
 *  孤零零地露在条边上。这个数字是排版底线，不是业务比例：在 375px 的条上，
 *  两位数百分比大约需要 25px 才不被裁；10% 那一段能分到约 31.5px，留了余量。
 *  低于它就不画，图例仍写着这段叫什么，stake 的分是三个数全部进了上面的
 *  说明段落，win 的分则由这条本身的 aria-label 兜底给屏幕阅读器。 */
const MIN_ON_BAR_LABEL_PCT = 10;

function onBarLabel(pct: number): string | null {
  return pct >= MIN_ON_BAR_LABEL_PCT ? formatFlowPct(pct) : null;
}

/** 每一段用哪个词条。写成查表而不是内联三元，是为了两处条共用一份映射。 */
function flowLabelKey(seg: FlowSegment): MessageKey {
  switch (seg.key) {
    case 'pool': return 'milharFlowPool';
    case 'fixed': return 'milharFlowFixed';
    case 'house': return 'milharFlowHouse';
    case 'winner': return 'milharFlowWinner';
    case 'seed': return 'milharFlowSeed';
  }
}
